<template>
  <md-dialog
    :md-active.sync="show"
    :md-close-on-esc="false"
    @md-opened="openDialog"
    @md-clicked-outside="closeDialog"
    @keyup.esc="closeDialog"
    @keyup.enter="submitDialog"
  >
    <md-dialog-title>{{ createOrEdit }} {{ scope.type | capitalize }} Policy</md-dialog-title>
    <div class="md-dialog-content md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <form>
        <md-field class="md-layout-item md-small-size-100 md-size-100">
          <label>Scope</label>
          <md-input v-model="scope.name" type="text" disabled></md-input>
        </md-field>

        <div class="md-layout md-alignment-center-space-between">
          <md-field class="md-layout-item md-small-size-100 md-size-45">
            <label>Parameter</label>
            <md-select v-model="current.param" @change="paramChanged">
              <md-option v-for="param in availableParams" v-bind:key="param.param" :value="param.param">
                {{ param.humanReadable }}
              </md-option>
            </md-select>
          </md-field>

          <md-field class="md-layout-item md-small-size-100 md-size-45">
            <label>Operators</label>
            <md-select v-model="current.operator" :disabled="!current.param">
              <md-option v-for="operator in availableOperators" v-bind:key="operator" :value="operator">
                {{ operator }}
              </md-option>
            </md-select>
          </md-field>
        </div>

        <md-field
          class="md-layout-item md-small-size-100 md-size-100"
          :class="{ 'md-invalid': editCreateError === 'condition' }"
        >
          <label v-if="current.param==='ipaddress'">Client IP</label>
          <label v-else>Operand</label>
          <md-input type="text" v-model="current.operand" :disabled="!current.param" @focus="clearErrors"></md-input>
          <md-icon v-if="numOfOperandHelpers > 1">info</md-icon>
          <span v-if="currentOperandHelper.details" class="md-helper-text">
            {{ currentOperandHelper.details }} E.g., {{ currentOperandHelper.example }}.
          </span>
          <span class="md-error">Not a valid policy condition!</span>
        </md-field>
        
        <md-field
          v-if="current.param==='ipaddress'"
          class="md-layout-item md-small-size-100 md-size-100"
          :class="{ 'md-invalid': editCreateError === 'condition' }"
        >
          <label>Application IP</label>
          <md-input type="text" v-model="current.ipOperand" :disabled="!current.param" @focus="clearErrors"></md-input>
          <md-icon v-if="numOfOperandHelpers > 1">info</md-icon>
          <span v-if="currentOperandHelper.details" class="md-helper-text">
            {{ currentOperandHelper.details }} E.g., {{ currentOperandHelper.example }}.
          </span>
          <span class="md-error">Not a valid policy condition!</span>
        </md-field>

        <md-field class="md-layout-item md-small-size-100 md-size-100">
          <policy-slider v-model="current.action" :disabled="!current.param"></policy-slider>
        </md-field>
      </form>

      <div class="md-layout-item md-size-100 text-right">
        <md-button @click="closeDialog">Close</md-button>
        <md-button class="md-primary" @click="submitDialog">{{ createOrEdit }}</md-button>
      </div>
    </div>
  </md-dialog>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import apiManager from "../../api-manager";
import fetchErrorHandler from "../../error";
import PolicySlider from "@/components/PolicySlider";
import camelCaseObjectKeysToSnakeCase from "@/utils/camelCaseObjectKeysToSnakeCase";

export default {
  name: "CreateEditPolicy",
  components: { PolicySlider },
  props: {
    show: {
      type: Boolean,
    },
    scope: {
      type: Object,
    },
  },
  setup(props, { emit }) {
    const openDialog = () => {
      current.value = {
        param: undefined,
        operator: undefined,
        operand: undefined,
        ipOperand: undefined,
        action: "mfa",
      };
      selectedOperandHelper.value = 0;
      clearErrors();
    };
    const closeDialog = () => {
      emit("close");
    };

    const editCreateError = ref("");
    const clearErrors = () => {
      editCreateError.value = "";
    };

    const submitDialog = async () => {
      clearErrors();

      const response = await apiManager.setPolicy(
        camelCaseObjectKeysToSnakeCase({
          params: {
            action: current.value.action === "bypass" ? "allow" : current.value.action,
            condition: currentPolicy.value,
            ...props.scope.params,
          },
        })
      );
      if (response.status === 200) emit("done");
      else if (response.status === 400) {
        editCreateError.value = response.parsedError;
      } else {
        fetchErrorHandler(response.status, response.statusText, "CreateEditPolicy");
      }
    };

    const createOrEdit = computed(() => {
      return props.scope.params.id ? "Edit" : "Create";
    });

    const current = ref({});
    const availableParams = [
      {
        param: "ipaddress",
        humanReadable: "Client IP Address",
        operators: ["in", "not in"],
        operandHelper: [
          {
            type: "text",
            humanReadableName: "IP Network",
            details: "Should be a IP Network in X.X.X.X/DD Pattern.",
            example: "192.168.0.0/16",
          },
        ],
      },
      {
        param: "timestamp",
        humanReadable: "TX Timestamp",
        operators: ["in", "not in"],
        operandHelper: [
          {
            type: "text",
            humanReadableName: "Time Range",
            details: "Should be a time-range.",
            example: "monday, sunday 10 to 12",
            format: '{0} {1} TimeRange("{2}")',
          },
        ],
      },
    ];
    const operatorsFor = (param) => {
      return availableParams.find((el) => el.param === param).operators;
    };
    const availableOperators = computed(() => {
      if (!current.value.param) return [];
      return availableParams.find((el) => el.param === current.value.param).operators;
    });
    const paramChanged = () => {
      selectedOperandHelper.value = 0;
      current.value.operand = undefined;
      current.value.ipOperand = undefined;
    };

    const selectedOperandHelper = ref(0);
    const currentOperandHelper = computed(() => {
      if (!current.value.param)
        return {
          mask: "",
          humanReadableName: "",
          details: "",
          example: "",
        };
      return availableParams.find((el) => el.param === current.value.param).operandHelper[selectedOperandHelper.value];
    });
    const numOfOperandHelpers = computed(() => {
      if (!current.value.param) return 0;
      return availableParams.find((el) => el.param === current.value.param).operandHelper.length;
    });

    const currentPolicy = computed(() => {
      if (current.value.param === "ipaddress") {
        const parts = [];
        if (current.value.operand) parts.push(`${current.value.param} ${current.value.operator} ${current.value.operand}`);
        if (current.value.ipOperand) parts.push(`app_ipaddress ${current.value.operator} ${current.value.ipOperand}`);
        return parts.join(" and ");
      }
      if (currentOperandHelper.value.format)
        return currentOperandHelper.value.format.format(
          current.value.param,
          current.value.operator,
          current.value.operand
        );

      return `${current.value.param} ${current.value.operator} ${current.value.operand}`;
    });

    return {
      openDialog,
      closeDialog,
      submitDialog,

      createOrEdit,
      editCreateError,
      clearErrors,

      availableParams,
      availableOperators,
      numOfOperandHelpers,
      operatorsFor,
      paramChanged,

      currentOperandHelper,
      selectedOperandHelper,

      current,
      currentPolicy,
    };
  },
};
</script>
